/** Находка — то, что человек пропустил, а данные показывают.
 *
 *  ОДНА ЗА РАЗ. Список приходит по убыванию важности, показываем первую. Две
 *  карточки подряд превращают находку в ленту уведомлений, а ленту закрывают не
 *  читая — вместе с настоящими.
 *
 *  ТОЛЬКО ТО, ГДЕ ЕСТЬ КНОПКА. Наблюдение без действия — шум. У каждой находки
 *  здесь есть что нажать, и каждый ответ что-то делает: записывает списание,
 *  гасит подписку, сливает дубль.
 *
 *  ИЩЕТ ЗАПРОС, А НЕ МОДЕЛЬ. `/api/findings` считает условия над данными и стоит
 *  ноль, поэтому дёргать его при каждом открытии не жалко, и бюджета ассистента
 *  это не трогает.
 */
import { useEffect, useState } from "react";
import { api, type Finding } from "../lib/api.ts";
import { dayLabel } from "../lib/group.ts";
import { formatMoney } from "../lib/money.ts";
import { plural } from "../lib/plural.ts";
import { useOnce } from "../lib/once.ts";
import { tap } from "../lib/telegram.ts";
import { card } from "./ui.tsx";

export function FindingCard({ onActed, onRecord }: {
  /** Что-то изменилось в данных — экрану пора перечитать себя. */
  onActed: () => void;
  /** Человек хочет записать трату руками: ставим курсор в поле ввода. */
  onRecord: () => void;
}) {
  const [items, setItems] = useState<Finding[]>([]);
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);
  const once = useOnce();

  useEffect(() => {
    api.findings().then((r) => setItems(r.items)).catch(() => setItems([]));
  }, []);

  const finding = hidden ? undefined : items[0];
  if (!finding) return null;

  /** Выполнить действие и перечитать экран. Ошибку отдельным текстом не
   *  показываем: находка вернётся при следующем открытии, и это честнее, чем
   *  красная строка поверх карточки, которая и так про чужую проблему. */
  const act = (run: () => Promise<unknown>) => {
    if (busy) return;
    tap();
    setBusy(true);
    once(() =>
      run()
        .then(() => { setHidden(true); onActed(); })
        .catch(() => setHidden(true))
        .finally(() => setBusy(false)),
    );
  };

  const later = () => { tap(); setHidden(true); };

  return (
    <div style={{ ...card, border: "1px solid #FFD42633" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8,
                    marginBottom: "var(--s-2)" }}>
        <Spark />
        <span style={{ flexGrow: 1, fontSize: "var(--f-caption)", color: "var(--warn)" }}>
          Находка
        </span>
        {items.length > 1 && (
          <span className="num" style={{ fontSize: "var(--f-caption-s)",
                                         color: "var(--text-faint)" }}>
            1 из {items.length}
          </span>
        )}
      </div>

      {finding.kind === "missed_charge" && (
        <Missed finding={finding} busy={busy} act={act} later={later} />
      )}
      {finding.kind === "duplicate" && (
        <Duplicate finding={finding} busy={busy} act={act} later={later} />
      )}
      {finding.kind === "silent" && (
        <Silent finding={finding} onRecord={onRecord} later={later} />
      )}
    </div>
  );
}

type Card = {
  finding: Finding;
  busy: boolean;
  act: (run: () => Promise<unknown>) => void;
  later: () => void;
};

function Missed({ finding, busy, act, later }: Card) {
  const sum = formatMoney(finding.amount ?? "0", finding.currency ?? "USD");
  const id = finding.subscription_id!;
  return (
    <>
      <Title>{finding.name} не списался</Title>
      <Explain>
        Срок был {dayLabel(finding.due_on ?? "")}, прошло{" "}
        {plural(finding.days_late ?? 0, ["день", "дня", "дней"])}, а записи нет.
        Либо забыли записать, либо подписки больше нет.
      </Explain>
      <Act busy={busy} onPick={() => act(() => api.paySubscription(id))}>
        Записать {sum}
      </Act>
      <Act quiet busy={busy}
           onPick={() => act(() => api.patchSubscription(id, { active: false }))}>
        Подписку отменил
      </Act>
      <Later onPick={later} />
    </>
  );
}

function Duplicate({ finding, busy, act, later }: Card) {
  const sum = formatMoney(finding.amount ?? "0", finding.currency ?? "USD");
  return (
    <>
      <Title>Две подписки «{finding.name}»</Title>
      <Explain>
        Обе на {sum}, списываются в один день месяца. Похоже на дубль — но
        одинаковые подписки бывают и настоящими парами, например два тарифа на
        разных аккаунтах.
      </Explain>
      <Act busy={busy} onPick={() => act(() => api.deleteSubscription(finding.drop_id!))}>
        Слить в одну
      </Act>
      <Later onPick={later} label="Это разные" />
    </>
  );
}

function Silent({ finding, onRecord, later }: {
  finding: Finding; onRecord: () => void; later: () => void;
}) {
  return (
    <>
      <Title>Записей нет {plural(finding.days_quiet ?? 0, ["день", "дня", "дней"])}</Title>
      <Explain>
        Последняя — {dayLabel(finding.last_record_on ?? "")}. Обычно вы вносите
        чаще; пропущенные траты потом не восстановить.
      </Explain>
      <Act onPick={() => { tap(); onRecord(); }}>Записать трату</Act>
      <Later onPick={later} />
    </>
  );
}

function Title({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: "var(--f-t3)", fontWeight: 700, letterSpacing: "-0.01em" }}>
      {children}
    </div>
  );
}

function Explain({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)",
                lineHeight: 1.5, margin: "var(--s-2) 0 var(--s-4)" }}>
      {children}
    </p>
  );
}

function Act({ children, onPick, quiet, busy }: {
  children: React.ReactNode; onPick: () => void; quiet?: boolean; busy?: boolean;
}) {
  return (
    <button type="button" onClick={onPick} aria-disabled={busy || undefined}
      style={{ display: "block", width: "100%", textAlign: "center",
               borderRadius: "var(--r-field)", border: "none",
               padding: "var(--pad-control)", marginBottom: "var(--s-2)",
               font: "inherit", fontSize: "var(--f-body)", fontWeight: 600,
               cursor: busy ? "default" : "pointer",
               background: quiet ? "var(--surface-2)" : "var(--accent)",
               color: quiet ? "var(--text)" : "var(--on-accent)",
               opacity: busy ? 0.6 : 1 }}>
      {children}
    </button>
  );
}

/** «Не сейчас» прячет находку до следующего открытия, а не навсегда.
 *  Пропущенное списание никуда не делось, и забыть о нём совсем было бы услугой
 *  сомнительного качества. */
function Later({ onPick, label = "Не сейчас" }: { onPick: () => void; label?: string }) {
  return (
    <button type="button" onClick={onPick}
      style={{ display: "block", width: "100%", background: "none", border: "none",
               color: "var(--text-faint)", font: "inherit",
               fontSize: "var(--f-caption)", padding: "var(--s-2) 0",
               minHeight: "var(--tap)", cursor: "pointer" }}>
      {label}
    </button>
  );
}

function Spark() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--warn)"
         strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3l1.9 4.8L19 9.7l-4.2 3 .7 5.3-3.5-2.6L8.5 18l.7-5.3-4.2-3 5.1-1.9z" />
    </svg>
  );
}
